/**
 * The door, before there is an account (schema: `held_drafts`).
 *
 * A stranger arriving at `nob.me/@handle` used to meet a sign-in screen. That
 * is backwards: they came to say something to somebody, and the first thing the
 * product did was ask them to prove who they are before they had written a
 * word. Now the composer is the first thing, and signing in is what SENDING
 * costs.
 *
 * What has not changed, and must not: nothing is sent, held or charged here.
 * This screen writes text somewhere it can be found again and then leaves for
 * the provider. There is no price to state (owner decision 2026-08-21) — a plan
 * is consented to when it is bought — so the plate carries the measured band as
 * PROSE and no figure at all (ruling CR). The intake, and the paywall for a
 * knocker with no room this period, still happen afterwards.
 *
 * IT IS THE DOOR PLATE, NOT A CHAT. This screen used to be a conversation: a
 * header bar and a run of doorman bubbles describing the door. It is now the
 * same printed plate the server renders at `nob.me/@handle` (`DoorCard`), with
 * the composer under it — because a stranger following a link has just been
 * looking at that plate, and answering them with a different object entirely was
 * the product losing its own thread. The bubble version also silently dropped
 * the owner's BIO, which is the one sentence a stranger most needs to read.
 *
 * TWO COPIES OF THE WORDS, on purpose. The server holds one under an anonymous
 * key (so the trip out to X and back finds it, in whatever tab it lands in) and
 * `lib/draft` keeps the other in this browser. The promise being made is "Nob
 * did not lose what you wrote", and a promise that depends on a network having
 * a good minute is not one worth making.
 */
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { holdDraft } from "../../lib/api";
import { anonKey, loadHeldKnock, saveHeldKnock, saveKnockTarget } from "../../lib/draft";
import { ApiError, doorQuery, handleExistsQuery } from "../../lib/queries";
import { continueWithX } from "../../lib/session";
import { TURN_MAX } from "../../lib/sse";
import { PosterBox, PosterMark, PosterPage, RegistrationMarks } from "../ui/poster";
import { Screen } from "../ui/screen";
import { SignInScreen } from "../onboarding/sign-in-screen";
import { MAY_BE_WAITING_COPY } from "../waiting/waiting-area";
import { DoorCard, DOOR_KICKER, DOOR_TAGLINE, LINK_COPIED, doorUrl } from "./door-card";

/** What happens next, stated before the button that does it. No price in it:
 * there is nothing to approve at this door — signing in is what sending costs. */
export const PRE_AUTH_NOTE =
  "Nothing is sent yet. Sign in and Nob asks you a few things, then this goes to the door.";
/**
 * PRD §2.3.4's own words, and they are load-bearing: the primary action at a
 * door reads "Knock with X" before there is a session, because the act is
 * KNOCKING and signing in is the toll on the way. Once they are back, with the
 * draft restored, the same action reads "Knock".
 */
export const CONTINUE_LABEL = "Knock with X";
/**
 * The ask, in the field itself, naming the person whose door it is — the
 * reference build's line. A named question gets a specific answer; "why you are
 * knocking" gets a paragraph about the sender.
 */
export function placeholderFor(name: string): string {
  const first = name.trim().split(/\s+/)[0];
  return first ? `Why should ${first}'s door open?` : PLACEHOLDER;
}

/** The fallback, for a door whose owner published no name at all. */
export const PLACEHOLDER = "Why you are knocking…";
export const PRE_AUTH_FIELD_LABEL = "Your knock";

/**
 * THE THREE DOORS THAT DO NOT OPEN, IN THE SERVER'S OWN WORDS.
 *
 * `apps/api/src/pages/door.ts` prints these three pages for a cold click with no
 * JS: a paused door, an identity that asked not to have a page (E-92a), and an
 * address with no door at it. This screen is the same address answered by the
 * SPA, so it says the same sentences — copy parity is the claim, and a second
 * wording for the same state would make the product's answer depend on which
 * renderer got there first.
 *
 * They are reached far more often than they used to be: "See it as strangers do"
 * lands here (owner item 14), so an owner who has taken their door down is shown
 * exactly what a stranger is shown (item 29) rather than a screen about them.
 */
/** The way off any of these screens — the same words the open poster uses. */
export const BACK_LABEL = "Back to Nob";
export const CLOSED_LINE = "This door is closed.";
export const SUPPRESSED_TITLE = "This page is not available.";
export const NOT_FOUND_TITLE = "No door here.";
export const NOT_FOUND_LINE = "Nob runs no namespace — doors live at their owner's X handle.";

/**
 * A HANDLE WITH NO DOOR IS A WAITING ROOM, NOT A DEAD END (owner item 10,
 * 2026-08-27).
 *
 * The server has always known this. `GET /@handle` routes a resolvable handle
 * that nobody has opened to `noNobPage` and keeps `notFoundPage` for the case
 * where X answers to nobody at all
 * (`apps/api/src/routes/public.ts`). The SPA collapsed both into one
 * `DOOR_NOT_FOUND` poster that said "no door here" and offered nothing — to the
 * one visitor the waiting room exists for: a stranger who followed a link,
 * wants to reach somebody, and has just been told there is nothing to do.
 *
 * So the poster asks the lookup the server asks (`handleExistsQuery`, which
 * answers rather than throwing) and mirrors the server's two pages. The copy is
 * the SSR page's, word for word, because that page and this one are the same
 * page served two ways and a visitor who reloads must not see it change its
 * mind.
 *
 * WHAT IT MAY SAY IS BOUNDED (waiting-room v1.4, H2 / FR-G2.2, ruling
 * R-0916-3). The handle string, the headline, the two ways on, and the standing
 * byline. No name, no avatar, no bio, no registration text, no registrant —
 * AND NO COUNT: there used to be a box here printing "N people are waiting to
 * reach this door", and ToU 2.2 §5A.5 says nobody outside the owner may learn
 * that. The line that replaced it is the signed-in room's own
 * (`MAY_BE_WAITING_COPY`), and it is unconditional, so it says the same thing
 * at a handle six people want as at one nobody has ever asked about.
 */
export const WAITING_TITLE = "No Nob here yet.";
export const WAITING_BYLINE =
  "Nob is a doorman for people with too much inbound. Nobody has opened this door.";
/**
 * The sticker. ONE SENTENCE, not two: it used to read "…them too" when the
 * count was above zero, which made the button itself a disclosure of whether
 * anybody was waiting (ruling R-0916-3).
 */
export const WAITING_HOLD = "I want to reach them";
export const WAITING_CLAIM = "Is this you? Open your Nob";
export const WAITING_FIELD_LABEL = "What do you want from them?";
export const WAITING_PLACEHOLDER = "One line: what do you want from them?";
/**
 * WHAT THIS IS NOT, said before the button rather than after it. It is the
 * signed-in room's own first paragraph (`waiting/waiting-area.tsx`), shortened
 * to a poster's one line: registering interest is not a knock, nothing is
 * assessed, and no money moves.
 */
export const WAITING_NOTE =
  "Not a knock, and free. You are telling us you want to reach them — nothing is assessed " +
  "and nothing is charged. Sign in with X and your spot is held.";

type Props = {
  handle: string;
  /** The way out of a door: home, as everywhere else. */
  onLeave: () => void;
};

/**
 * A door that did not open, printed the way the server prints it.
 *
 * The plate loses everything the door was going to say — no About box, no *What
 * gets through*, no difficulty, no composer — because a shut door is an address
 * and not a plate (`pages/door.ts#closedPage`). What it keeps is the kicker, the
 * wordmark, the address and one line saying which of the three this is.
 *
 * `handle` is what a CLOSED door still shows: the identity resolves, it simply
 * does not open. The other two are deliberately mute about it — a suppressed
 * identity gets no detail (E-92a) and an address with no door has no owner to
 * name.
 */
function ShutPoster({
  handle,
  title,
  line,
  onLeave,
}: {
  handle?: string;
  title?: string;
  line?: string;
  onLeave: () => void;
}) {
  return (
    <PosterPage about={false}>
      <div className="nob-poster-card">
        <RegistrationMarks />
        <p className="nob-poster-card-kicker">{DOOR_KICKER}</p>
        <PosterMark />
        <div className="nob-poster-who">
          <p className="nob-poster-name">{title ?? `@${handle ?? ""}`}</p>
        </div>
        {line && <p className="nob-poster-aside">{line}</p>}
        <div className="nob-poster-foot">
          {handle ? (
            <span className="nob-poster-url">{doorUrl(handle)}</span>
          ) : (
            <span className="nob-poster-url" />
          )}
          <span className="nob-poster-tag">{DOOR_TAGLINE}</span>
        </div>
      </div>
      <p className="nob-poster-footnote">
        <button type="button" className="nob-poster-inline" onClick={onLeave}>
          {BACK_LABEL}
        </button>
      </p>
    </PosterPage>
  );
}

/**
 * The waiting room, printed for a stranger who is not signed in.
 *
 * THE LINE IS TAKEN HERE AND HELD LOCALLY, for the same reason the live door's
 * composer takes the knock before the sign-in: what somebody wrote is the work
 * worth keeping, and asking them to prove who they are before they have said
 * anything is the order this product refuses. `saveHeldKnock` + `saveKnockTarget`
 * are the machinery the live door already uses — the return trip lands on this
 * handle, the console opens the waiting room for it (`compose-pane.tsx`), and
 * that room seeds its own field from the same held line.
 *
 * NOTHING IS SENT FROM HERE. Registering interest needs a session (the endpoint
 * answers 401 without one), so this screen writes to the browser and leaves for
 * X. There is no anonymous hold on the server either — `holdDraft` is a draft
 * against a DOOR, and the whole point of this page is that there is no door.
 */
function WaitingPoster({ handle, onLeave }: { handle: string; onLeave: () => void }) {
  /** Whatever this browser was holding for THIS handle, so a bounced tab keeps it. */
  const held = useRef(loadHeldKnock());
  const [line, setLine] = useState(held.current?.handle === handle ? held.current.body : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const leave = useCallback(
    (written: string | null) => {
      if (busy) return;
      setBusy(true);
      setError(null);
      void (async () => {
        // The local copy first — it is the one that cannot fail.
        if (written) {
          saveHeldKnock({ handle, body: written });
          saveKnockTarget(handle);
        }
        const message = await continueWithX();
        if (message === null) return; // already on the way out
        setBusy(false);
        setError(message);
      })();
    },
    [busy, handle],
  );

  return (
    <PosterPage about={false}>
      <div className="nob-poster-card">
        <RegistrationMarks />
        <p className="nob-poster-card-kicker">{DOOR_KICKER}</p>
        <PosterMark />
        <div className="nob-poster-who">
          <p className="nob-poster-name">{WAITING_TITLE}</p>
          <p className="nob-poster-handle">@{handle}</p>
        </div>

        {/* NO COUNT (ruling R-0916-3). The box stays, because the honest
            sentence about an unopened handle is worth saying and the poster has
            a place for it; what it may not carry is a figure. It is drawn
            unconditionally, which is the point — a box that appeared only when
            somebody was waiting would be the count in another form. */}
        <PosterBox kicker="Waiting">
          <p>{MAY_BE_WAITING_COPY}</p>
        </PosterBox>

        <form
          className="nob-poster-form"
          onSubmit={(e) => {
            e.preventDefault();
            const written = line.trim();
            if (written.length < 3) return;
            leave(written);
          }}
        >
          <label htmlFor="pre-auth-waiting" className="sr-only">
            {WAITING_FIELD_LABEL}
          </label>
          <input
            id="pre-auth-waiting"
            type="text"
            maxLength={280}
            value={line}
            placeholder={WAITING_PLACEHOLDER}
            aria-label={WAITING_FIELD_LABEL}
            onChange={(e) => setLine(e.target.value)}
            className="nob-poster-input"
          />
          <button
            type="submit"
            className="nob-sticker-btn"
            disabled={busy || line.trim().length < 3}
          >
            {WAITING_HOLD}
          </button>
          <p className="nob-poster-note">{WAITING_NOTE}</p>
          {error && (
            <p className="nob-poster-error" role="alert">
              {error}
            </p>
          )}
        </form>

        {/* The other way on, and it is the one the OWNER takes. Same road out —
            X is the only way in (ruling R-0826-1) — and no line to hold. */}
        <p className="nob-poster-hint">
          <button
            type="button"
            className="nob-poster-inline"
            disabled={busy}
            onClick={() => leave(null)}
          >
            {WAITING_CLAIM}
          </button>
        </p>

        <p className="nob-poster-aside">{WAITING_BYLINE}</p>
      </div>
      <p className="nob-poster-footnote">
        <button type="button" className="nob-poster-inline" onClick={onLeave}>
          {BACK_LABEL}
        </button>
      </p>
    </PosterPage>
  );
}

/**
 * Which of the two "there is no door" pages this address is.
 *
 * The door read has already failed with `DOOR_NOT_FOUND`, which the server uses
 * for both "nobody on X answers to this" and "somebody does, and has not opened
 * a door". `handleExistsQuery` is the FREE check that tells them apart (ruling
 * R-0910-2 #2 and #3): Nob's own records, its miss cache, then X's keyless embed
 * endpoint. NOBODY SIGNED OUT EVER CAUSES A PAID CALL, and this page is the
 * whole reason that rule needed writing down — it is the one surface a stranger
 * can put any address into, from any link, as many times as they like.
 *
 * THREE ANSWERS, TWO PAGES. Somebody holds the address, so the no-Nob poster;
 * nobody does, or X would not say, so the mute one. The two negatives are drawn
 * the same on purpose: a page that invited somebody to wait at a handle that
 * does not exist would be inventing a person, and one that did it on the
 * strength of an outage would be inventing them out of nothing at all.
 */
function NoDoorPoster({ handle, onLeave }: { handle: string; onLeave: () => void }) {
  const lookup = useQuery(handleExistsQuery(handle));

  if (lookup.isPending) {
    return (
      <Screen>
        <p className="nob-breathe text-sm text-faint">Finding the door.</p>
      </Screen>
    );
  }
  if (lookup.data?.exists === true) {
    return <WaitingPoster handle={handle} onLeave={onLeave} />;
  }
  return <ShutPoster title={NOT_FOUND_TITLE} line={NOT_FOUND_LINE} onLeave={onLeave} />;
}

export function PreAuthKnock({ handle, onLeave }: Props) {
  const door = useQuery(doorQuery(handle));

  /** Whatever this browser was holding for THIS door, so a bounced tab keeps it. */
  const restored = useRef(loadHeldKnock());
  const mine = restored.current?.handle === handle ? restored.current.body : "";

  const [body, setBody] = useState(mine);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** A self-clearing acknowledgement — today only "the link is copied". */
  const [note, setNote] = useState<string | null>(null);

  const field = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = field.current;
    if (!el) return;
    el.style.height = "0px";
    const border = el.offsetHeight - el.clientHeight;
    el.style.height = `${Math.min(el.scrollHeight + border, 208)}px`;
  }, [body]);

  /**
   * Hold the words, then leave. In that order and never the other way: the
   * whole point is that the trip out is survivable, and a browser already on
   * its way to X cannot be relied on to finish a request.
   */
  const hold = useCallback(async (): Promise<void> => {
    const written = body.trim();
    if (!written) return;
    // The local copy first — it is the one that cannot fail.
    saveHeldKnock({ handle, body: written });
    // And the door, so the return trip (which lands on a bare `/`) comes back
    // here rather than to somebody's own onboarding. The route stores this on
    // arrival too; writing it again at the moment of leaving costs a
    // localStorage line and removes the question of whether it survived.
    saveKnockTarget(handle);
    await holdDraft({ anon_key: anonKey(), door_handle: handle, body: written });
  }, [body, handle]);

  const continueOut = useCallback(
    (go: () => Promise<string | null>) => {
      if (busy || body.trim().length === 0) return;
      setBusy(true);
      setError(null);
      void (async () => {
        await hold();
        const message = await go();
        // A `null` means the browser is already leaving; anything else is the
        // road out having failed, and the words are safe either way.
        if (message === null) return;
        setBusy(false);
        setError(message);
      })();
    },
    [body, busy, handle, hold],
  );

  if (door.isPending) {
    return (
      <Screen>
        <p className="nob-breathe text-sm text-faint">Finding the door.</p>
      </Screen>
    );
  }

  // A door that will not open for a stranger — paused, gone, never there — has
  // nothing to write to, and a composer over it would be a promise nobody can
  // keep. Each of the three refusals gets the PRINTED page's answer, by its
  // code; anything else (a network that would not answer, a shape we do not
  // know) falls back to the ordinary signed-out screen rather than telling
  // somebody their door is gone on the strength of a dropped connection.
  if (!door.data) {
    const code = door.error instanceof ApiError ? door.error.code : null;
    if (code === "DOOR_CLOSED") {
      return <ShutPoster handle={handle} line={CLOSED_LINE} onLeave={onLeave} />;
    }
    if (code === "DOOR_SUPPRESSED") {
      return <ShutPoster title={SUPPRESSED_TITLE} onLeave={onLeave} />;
    }
    // NOT a dead end. `DOOR_NOT_FOUND` is two situations wearing one code, and
    // the second of them is what the waiting room is for — see `NoDoorPoster`.
    if (code === "DOOR_NOT_FOUND") {
      return <NoDoorPoster handle={handle} onLeave={onLeave} />;
    }
    return <SignInScreen />;
  }

  return (
    <PosterPage about={false}>
      <DoorCard
        door={door.data}
        onCopyLink={() => {
          void navigator.clipboard?.writeText(`${window.location.origin}/@${handle}`);
          setNote(LINK_COPIED);
        }}
      >
        {/* THE BOX IS OPEN TO ANYONE, and writing is the work worth keeping, so
            it happens before the sign-in rather than after it. */}
        <form
          className="nob-poster-form"
          onSubmit={(e) => {
            e.preventDefault();
            continueOut(() => continueWithX());
          }}
        >
          <label htmlFor="pre-auth-knock" className="sr-only">
            {PRE_AUTH_FIELD_LABEL}
          </label>
          <textarea
            id="pre-auth-knock"
            ref={field}
            rows={3}
            value={body}
            maxLength={TURN_MAX}
            placeholder={placeholderFor(door.data.display_name)}
            aria-label={PRE_AUTH_FIELD_LABEL}
            onChange={(e) => setBody(e.target.value)}
            className="nob-poster-textarea"
          />
          <button
            type="submit"
            className="nob-sticker-btn"
            disabled={busy || body.trim().length === 0}
          >
            {CONTINUE_LABEL}
          </button>
          {/* There is no second lane under this button. X is the only way in
              (ruling R-0826-1), so a door offers one road and says so. */}
          <p className="nob-poster-note">{PRE_AUTH_NOTE}</p>
          {note && (
            <p className="nob-poster-aside" role="status">
              {note}
            </p>
          )}
          {error && (
            <p className="nob-poster-error" role="alert">
              {error}
            </p>
          )}
        </form>
      </DoorCard>
      <p className="nob-poster-footnote">
        <button type="button" className="nob-poster-inline" onClick={onLeave}>
          {BACK_LABEL}
        </button>
      </p>
    </PosterPage>
  );
}
